import React from "react";
import cx from "classnames";
import { Box, SelectChangeEvent, styled } from "@mui/material";

import AccountsProcessingAndInvoiceStatusSelect from "../../../FundedTrader/components/AccountsProcessingAndInvoiceStatusSelect";
import {
  EInvoiceStatusesLiveSimFundedTrader,
  EProcessingStatuses,
  EVALUATION_TYPE_OPTIONS,
  INVOICE_LIVE_SIM_STATUSES_OPTIONS,
  INVOICE_LIVE_STATUSES_OPTIONS,
  PROCESSING_STATUSES_OPTIONS,
  DEPRECATED_INVOICE_STATUSES_OPTIONS,
  EDeprecatedInvoiceStatusesLiveFundedTrader,
} from "../../../../constants";
import { ITradedAccount } from "../../../../api/funds/index.types";
import { IDialogConfirmProps } from "../FundedTradersTable/TableBody";

import {
  INVOICE_STATUSES_IN_PROGRESS,
  INVOICE_STATUSES_READY,
  PROCESSING_STATUSES_IN_PROGRESS,
  PROCESSING_STATUSES_READY,
} from "./utils";
import { checkIsCompletedAccount } from "../../../FundedTrader/utils";
import s from "./styles.module.css";
interface IProps {
  traded_accounts: ITradedAccount[];
  setDialogConfirmProps: (v: IDialogConfirmProps) => void;
  id: number;
  invoice_status: string | null;
  processing_status: string | null;
}

const NestedTable = ({ children }: { children: React.ReactNode }) => {
  return (
    <td>
      <Box
        component="table"
        sx={{
          width: "100%",
          "& td": {
            background: "none !important",
          },
          borderCollapse: "collapse",
        }}
      >
        <tbody>{children}</tbody>
      </Box>
    </td>
  );
};

const Td = styled("td")({ height: "50px" });
const E2tAccountID = styled("td")({
  height: 50,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
});

export const getDialogContentText = (
  name: string,
  e2t_account_id: string,
  value: string
): { dialogTitle: string; dialogContentText: string } => {
  const baseTitle = `Are you sure you want to change Account ID "${e2t_account_id}"`;
  if (name === "processing_status") {
    if (checkIsCompletedAccount(value as EProcessingStatuses)) {
      const newProcessingStatus = (value as EProcessingStatuses).toUpperCase();
      return {
        dialogTitle: `Change status to ${newProcessingStatus}`,
        dialogContentText: `Are you sure you want to change the processing status to 
          ${newProcessingStatus}? This operation is non-reversible.`,
      };
    } else {
      return {
        dialogTitle: "Change processing status",
        dialogContentText: `${baseTitle} processing status ?`,
      };
    }
  }

  const dialogData = {
    invoice_status: {
      dialogTitle: "Change invoice status",
      dialogContentText: `${baseTitle} invoice status ?`,
    },
    evaluation_type: {
      dialogTitle: "Change evaluation type",
      dialogContentText: `${baseTitle} evaluation type ?`,
    },
  };
  return dialogData[name as keyof typeof dialogData];
};

const TradedAccountsInfoTableColumns: React.FC<IProps> = ({
  traded_accounts,
  setDialogConfirmProps,
  id,
  invoice_status,
  processing_status,
}) => {
  const handleChangeStatus = (
    e: SelectChangeEvent<string>,
    id: number,
    accounts_id: number,
    e2t_account_id: string,
    isEditInitialProcStatusAndInvoiceStatus = false
  ) => {
    const name = e.target.name as string;
    const value = e.target.value as string;

    setDialogConfirmProps({
      isOpen: true,
      dialogTitle: getDialogContentText(name, e2t_account_id, value).dialogTitle,
      dialogContentText: getDialogContentText(name, e2t_account_id, value).dialogContentText,
      id,
      accounts_id,
      name,
      value,
      isEditInitialProcStatusAndInvoiceStatus,
    });
  };

  const checkIsProcessingStatusIsReady = (processing_status: EProcessingStatuses) =>
    PROCESSING_STATUSES_READY.includes(processing_status);
  const checkIsInvoiceStatusIsReady = (invoice_status: EInvoiceStatusesLiveSimFundedTrader) =>
    INVOICE_STATUSES_READY.includes(invoice_status);

  const liveAndLiveSimPotions = [
    ...INVOICE_LIVE_SIM_STATUSES_OPTIONS,
    ...INVOICE_LIVE_STATUSES_OPTIONS,
  ].reduce((acc, option) => {
    if (!acc.some((e: { value: string }) => e.value === option.value)) {
      // @ts-ignore
      acc.push(option);
    }
    return acc;
  }, []);

  const deprecatedInvoiceStatusesOptions = DEPRECATED_INVOICE_STATUSES_OPTIONS.map((e) => e.value);

  return (
    <>
      <NestedTable>
        <tr>
          <E2tAccountID />
        </tr>
        {traded_accounts.map((account) => (
          <tr key={account.id}>
            <Td>
              <AccountsProcessingAndInvoiceStatusSelect
                useSmallSize
                bgStylesClass={cx({
                  [s.inProgressStatus]: true,
                  [s.readyStatus]: checkIsProcessingStatusIsReady(account.processing_status),
                  [s.failedStatus]: account.processing_status === EProcessingStatuses.failed,
                })}
                options={EVALUATION_TYPE_OPTIONS}
                name="evaluation_type"
                value={account.evaluation_type}
                id={account.id}
                accounts_id={account.accounts_id}
                handleChange={(e, id, accounts_id) => {
                  handleChangeStatus(e, id, accounts_id, account.e2t_account_id);
                }}
              />
            </Td>
          </tr>
        ))}
      </NestedTable>

      <NestedTable>
        <tr>
          <E2tAccountID />
        </tr>
        {traded_accounts.map((account) => (
          <tr key={account.id}>
            <E2tAccountID>
              <Box
                component="span"
                className={cx({
                  [s.regularStatus]: true,
                  [s.readyStatus]:
                    checkIsProcessingStatusIsReady(account.processing_status) &&
                    checkIsInvoiceStatusIsReady(account.invoice_status),
                  [s.failedReviewStatus]: account.processing_status === EProcessingStatuses.failed,
                  [s.inProgressStatus]:
                    (PROCESSING_STATUSES_IN_PROGRESS.includes(account.processing_status) &&
                      INVOICE_STATUSES_IN_PROGRESS.includes(account.invoice_status)) ||
                    !checkIsProcessingStatusIsReady(account.processing_status) ||
                    !checkIsInvoiceStatusIsReady(account.invoice_status),
                })}
                sx={{
                  display: "block",
                  padding: "7px 10px",
                  minWidth: "100%",
                  textAlign: "center",
                  borderRadius: "16px",
                }}
              >
                {account.e2t_account_id || "N/A"}
              </Box>
            </E2tAccountID>
          </tr>
        ))}
      </NestedTable>

      <NestedTable>
        <tr>
          {/*INITIAL processing_status*/}
          <Td>
            <AccountsProcessingAndInvoiceStatusSelect
              useSmallSize
              options={PROCESSING_STATUSES_OPTIONS}
              name="processing_status"
              value={processing_status || ""}
              id={id}
              accounts_id={id}
              handleChange={(e, id, accounts_id) => {
                handleChangeStatus(e, id, accounts_id, "", true);
              }}
            />
          </Td>
        </tr>
        {traded_accounts.map((account) => (
          <tr key={account.id}>
            <Td>
              <AccountsProcessingAndInvoiceStatusSelect
                useSmallSize
                bgStylesClass={cx({
                  [s.inProgressStatus]: true,
                  [s.readyStatus]: checkIsProcessingStatusIsReady(account.processing_status),
                  [s.failedStatus]: account.processing_status === EProcessingStatuses.failed,
                })}
                options={PROCESSING_STATUSES_OPTIONS}
                name="processing_status"
                value={account.processing_status}
                id={account.id}
                accounts_id={account.accounts_id}
                handleChange={(e, id, accounts_id) => {
                  handleChangeStatus(e, id, accounts_id, account.e2t_account_id);
                }}
              />
            </Td>
          </tr>
        ))}
      </NestedTable>

      <NestedTable>
        <tr>
          {/*INITIAL invoice_status*/}
          <Td>
            <AccountsProcessingAndInvoiceStatusSelect
              useSmallSize
              options={liveAndLiveSimPotions}
              name="invoice_status"
              value={invoice_status || ""}
              id={id}
              accounts_id={id}
              handleChange={(e, id, accounts_id) => {
                handleChangeStatus(e, id, accounts_id, "", true);
              }}
            />
          </Td>
        </tr>
        {traded_accounts.map((account) => (
          <tr key={account.id}>
            <Td>
              <AccountsProcessingAndInvoiceStatusSelect
                disabled={deprecatedInvoiceStatusesOptions.includes(
                  account.invoice_status as unknown as EDeprecatedInvoiceStatusesLiveFundedTrader
                )}
                useSmallSize
                bgStylesClass={cx({
                  [s.inProgressStatus]: true,
                  [s.readyStatus]: checkIsInvoiceStatusIsReady(account.invoice_status),
                })}
                options={
                  account.account_type === "LiveSim"
                    ? [...INVOICE_LIVE_SIM_STATUSES_OPTIONS, ...DEPRECATED_INVOICE_STATUSES_OPTIONS]
                    : [...INVOICE_LIVE_STATUSES_OPTIONS, ...DEPRECATED_INVOICE_STATUSES_OPTIONS]
                }
                name="invoice_status"
                value={account.invoice_status || ""}
                id={account.id}
                accounts_id={account.accounts_id}
                handleChange={(e, id, accounts_id) => {
                  handleChangeStatus(e, id, accounts_id, account.e2t_account_id);
                }}
              />
            </Td>
          </tr>
        ))}
      </NestedTable>
    </>
  );
};

export default TradedAccountsInfoTableColumns;
